import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { creditNoteService } from '../../../api/apiService';
import { userService } from '../../../api/apiService';
import '../../../App.css';
import './TalentCreditNotesList.css';

/**
 * Credit notes for talents managed by the current user (agency / multiple talents).
 * Only shown when the user has multiple talent profiles. Read-only: list + PDF/XML download.
 * Includes filters (talent, agency, period) and CSV export.
 */
function TalentCreditNotesList() {
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchLimit, setFetchLimit] = useState(200);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState({});
  const [talentCount, setTalentCount] = useState(0);

  // Filters
  const [filterSearch, setFilterSearch] = useState('');
  const [filterTalent, setFilterTalent] = useState('');
  const [filterAgency, setFilterAgency] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('all'); // all | week | month | year | custom
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const doFetch = useCallback((limit) => {
    setLoading(true);
    setError(null);
    creditNoteService.getCreditNotesForMyTalents({ limit })
      .then((res) => setCreditNotes(res.data?.creditNotes || []))
      .catch((err) => {
        console.error('Error loading credit notes:', err);
        setError(err.message || 'Failed to load credit notes');
        setCreditNotes([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setError(null);
      try {
        const profileRes = await userService.getProfile();
        const userData = profileRes?.data?.user || profileRes?.data;
        const talents = userData?.talents || [];
        setTalentCount(talents.length);
        const res = await creditNoteService.getCreditNotesForMyTalents({ limit: 200 });
        setCreditNotes(res.data?.creditNotes || []);
      } catch (err) {
        console.error('Error loading credit notes:', err);
        setError(err.message || 'Failed to load credit notes');
        setCreditNotes([]);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  // When period needs a larger dataset, bump the limit and re-fetch
  useEffect(() => {
    if ((filterPeriod === 'year' || filterPeriod === 'custom') && fetchLimit < 5000) {
      setFetchLimit(5000);
      doFetch(5000);
    }
  }, [filterPeriod]);

  const formatDate = (d) => {
    if (!d) return '—';
    const date = typeof d === 'string' ? new Date(d) : d;
    return isNaN(date.getTime()) ? String(d) : date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatTime = (d) => {
    if (!d) return '—';
    const date = typeof d === 'string' ? new Date(d) : d;
    if (isNaN(date.getTime())) return String(d);
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const formatMoney = (n) => {
    if (n == null || n === '') return '—';
    const num = typeof n === 'number' ? n : parseFloat(n);
    return isNaN(num) ? String(n) : num.toFixed(2);
  };

  const displayAgency = (name) => {
    const s = name != null ? String(name).trim() : '';
    if (s.length <= 1) return '—';
    return s;
  };

  // Period helpers
  const getWeekBounds = () => {
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    return { from: monday, to: sunday };
  };

  const getMonthBounds = () => {
    const now = new Date();
    return {
      from: new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0),
      to: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999),
    };
  };

  const getYearBounds = () => {
    const now = new Date();
    return {
      from: new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0),
      to: new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999),
    };
  };

  const matchesPeriod = (cn) => {
    if (filterPeriod === 'all') return true;
    const dateStr = cn.created_at || cn.invoice_date;
    if (!dateStr) return false;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return false;
    if (filterPeriod === 'week') { const b = getWeekBounds(); return d >= b.from && d <= b.to; }
    if (filterPeriod === 'month') { const b = getMonthBounds(); return d >= b.from && d <= b.to; }
    if (filterPeriod === 'year') { const b = getYearBounds(); return d >= b.from && d <= b.to; }
    if (filterPeriod === 'custom') {
      if (filterDateFrom) { const from = new Date(filterDateFrom); from.setHours(0, 0, 0, 0); if (d < from) return false; }
      if (filterDateTo) { const to = new Date(filterDateTo); to.setHours(23, 59, 59, 999); if (d > to) return false; }
      return true;
    }
    return true;
  };

  // Smart dropdown options — derived from loaded data
  const uniqueTalents = useMemo(() => {
    const s = new Set(creditNotes.map(cn => cn.talent_name).filter(Boolean));
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [creditNotes]);

  const uniqueAgencies = useMemo(() => {
    const s = new Set(
      creditNotes
        .map(cn => (cn.agency_name != null ? String(cn.agency_name).trim() : ''))
        .filter(v => v.length > 1)
    );
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [creditNotes]);

  /**
   * Extract client name from a credit note object.
   * client_name lives on credit_note_items in the DB, so the backend must JOIN it.
   * We try several paths in case the backend returns it differently:
   *   cn.client_name          — backend JOINs items and aliases to root level (preferred)
   *   cn.items[0].client_name — backend returns nested items array
   *   cn.client               — backend returns raw booking client field
   */
  const getClientName = (cn) => {
    if (cn.client_name) return cn.client_name;
    if (Array.isArray(cn.items) && cn.items[0]?.client_name) return cn.items[0].client_name;
    if (typeof cn.client === 'string' && cn.client.length > 1) return cn.client;
    if (cn.client && typeof cn.client === 'object') return cn.client.name || cn.client.label || null;
    return null;
  };

  const uniqueClients = useMemo(() => {
    const s = new Set(creditNotes.map(cn => getClientName(cn)).filter(Boolean));
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [creditNotes]);

  const filtered = useMemo(() => {
    const search = filterSearch.toLowerCase();
    return creditNotes.filter(cn => {
      if (filterTalent && cn.talent_name !== filterTalent) return false;
      if (filterAgency) {
        const ag = cn.agency_name != null ? String(cn.agency_name).trim() : '';
        if (ag !== filterAgency) return false;
      }
      if (filterClient && getClientName(cn) !== filterClient) return false;
      if (search) {
        const haystack = [cn.credit_note_number, cn.talent_name, cn.agency_name, getClientName(cn), cn.created_by]
          .filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return matchesPeriod(cn);
    });
  }, [creditNotes, filterSearch, filterTalent, filterAgency, filterClient, filterPeriod, filterDateFrom, filterDateTo]);

  const sumMoney = (list, field) =>
    list.reduce((acc, cn) => acc + (parseFloat(cn[field]) || 0), 0);

  const filteredTotals = useMemo(() => ({
    count: filtered.length,
    net: sumMoney(filtered, 'net_amount'),
    vat: sumMoney(filtered, 'vat_amount'),
    gross: sumMoney(filtered, 'gross_amount'),
  }), [filtered]);

  const periodTotals = useMemo(() => {
    const inPeriod = (list, bounds) =>
      list.filter(cn => {
        const dateStr = cn.created_at || cn.invoice_date;
        if (!dateStr) return false;
        const d = new Date(dateStr);
        return !isNaN(d.getTime()) && d >= bounds.from && d <= bounds.to;
      });
    const week = inPeriod(creditNotes, getWeekBounds());
    const month = inPeriod(creditNotes, getMonthBounds());
    const year = inPeriod(creditNotes, getYearBounds());
    return {
      week:  { count: week.length,  gross: sumMoney(week, 'gross_amount') },
      month: { count: month.length, gross: sumMoney(month, 'gross_amount') },
      year:  { count: year.length,  gross: sumMoney(year, 'gross_amount') },
    };
  }, [creditNotes]);

  const handleExportCsv = () => {
    const headers = ['Number', 'Type', 'Status', 'Working Dates', 'Created', 'Created By', 'Talent', 'Client', 'Agency', 'AP %', 'Net', 'VAT', 'Gross'];
    const escape = (v) => {
      const s = v == null ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = filtered.map(cn => [
      cn.credit_note_number || cn.id,
      cn.invoice_type || '',
      cn.status || '',
      cn.booking_dates ? cn.booking_dates.replace(/\n/g, ' | ') : formatDate(cn.invoice_date),
      cn.created_at ? `${formatDate(cn.created_at)} ${formatTime(cn.created_at)}` : '',
      cn.created_by || '',
      cn.talent_name || '',
      getClientName(cn) || '',
      displayAgency(cn.agency_name),
      cn.agency_percentage != null ? `${Number(cn.agency_percentage)}%` : '',
      formatMoney(cn.net_amount),
      formatMoney(cn.vat_amount),
      formatMoney(cn.gross_amount),
    ].map(escape).join(','));
    const csv = [headers.join(','), ...rows].join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv; charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `credit-notes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = async (cn) => {
    const key = `pdf-${cn.id}`;
    setDownloading(prev => ({ ...prev, [key]: true }));
    try {
      const res = await creditNoteService.getCreditNotePdf(cn.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Gutschrift_${cn.credit_note_number || cn.id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed:', err);
    } finally {
      setDownloading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleDownloadXml = async (cn) => {
    const key = `xml-${cn.id}`;
    setDownloading(prev => ({ ...prev, [key]: true }));
    try {
      const res = await creditNoteService.getCreditNoteXml(cn.id);
      const blob = new Blob([res.data], { type: 'application/xml; charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Gutschrift_${cn.credit_note_number || cn.id}.xml`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('XML download failed:', err);
    } finally {
      setDownloading(prev => ({ ...prev, [key]: false }));
    }
  };

  const resetFilters = () => {
    setFilterSearch('');
    setFilterTalent('');
    setFilterAgency('');
    setFilterClient('');
    setFilterPeriod('all');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  if (loading) {
    return (
      <div className="content-wrapper page">
        <div className="content-wrapper">
          <h1 className="h1">Credit notes</h1>
          <p className="talent-cn-loading">Loading{fetchLimit > 200 ? ' full dataset…' : '…'}</p>
        </div>
      </div>
    );
  }

  if (talentCount <= 1) {
    return (
      <div className="content-wrapper page">
        <div className="content-wrapper">
          <h1 className="h1">Credit notes</h1>
          <p className="talent-cn-info">
            This view is for agency accounts that manage multiple talents. You have one talent profile linked. Credit notes for your bookings are available from your main Projects view and from the admin team.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="content-wrapper page">
      <div className="content-wrapper">
        <h1 className="h1">Credit notes</h1>

        {/* Period report summary */}
        <div className="cn-report-row">
          <div className="cn-report-card">
            <span className="cn-report-label">This week</span>
            <span className="cn-report-count">{periodTotals.week.count} notes</span>
            <span className="cn-report-gross">{periodTotals.week.gross.toFixed(2)} €</span>
          </div>
          <div className="cn-report-card">
            <span className="cn-report-label">This month</span>
            <span className="cn-report-count">{periodTotals.month.count} notes</span>
            <span className="cn-report-gross">{periodTotals.month.gross.toFixed(2)} €</span>
          </div>
          <div className="cn-report-card cn-report-card--total">
            <span className="cn-report-label">This year</span>
            <span className="cn-report-count">{periodTotals.year.count} notes</span>
            <span className="cn-report-gross">{periodTotals.year.gross.toFixed(2)} €</span>
          </div>
        </div>

        {/* Filters */}
        <div className="cn-filters">
          <input
            className="cn-filter-input"
            type="text"
            placeholder="Search number…"
            value={filterSearch}
            onChange={e => setFilterSearch(e.target.value)}
          />
          <select className="cn-filter-select" value={filterTalent} onChange={e => setFilterTalent(e.target.value)}>
            <option value="">All talents</option>
            {uniqueTalents.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select className="cn-filter-select" value={filterAgency} onChange={e => setFilterAgency(e.target.value)}>
            <option value="">All agencies</option>
            {uniqueAgencies.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select className="cn-filter-select" value={filterClient} onChange={e => setFilterClient(e.target.value)}>
            <option value="">All clients</option>
            {uniqueClients.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="cn-filter-select" value={filterPeriod} onChange={e => setFilterPeriod(e.target.value)}>
            <option value="all">All time</option>
            <option value="week">This week</option>
            <option value="month">This month</option>
            <option value="year">This year</option>
            <option value="custom">Custom range</option>
          </select>
          {filterPeriod === 'custom' && (
            <>
              <input
                className="cn-filter-input cn-filter-date"
                type="date"
                value={filterDateFrom}
                onChange={e => setFilterDateFrom(e.target.value)}
              />
              <input
                className="cn-filter-input cn-filter-date"
                type="date"
                value={filterDateTo}
                onChange={e => setFilterDateTo(e.target.value)}
              />
            </>
          )}
          <button type="button" className="button cn-filter-reset" onClick={resetFilters}>
            Reset
          </button>
          <button type="button" className="button cn-export-csv-btn" onClick={handleExportCsv}>
            Export CSV ({filtered.length})
          </button>
        </div>

        {error && (
          <div className="talent-cn-error">{error}</div>
        )}

        {/* Filtered totals */}
        <div className="cn-filtered-totals">
          <span>{filtered.length} credit note{filtered.length !== 1 ? 's' : ''}</span>
          <span>Net: <strong>{filteredTotals.net.toFixed(2)} €</strong></span>
          <span>VAT: <strong>{filteredTotals.vat.toFixed(2)} €</strong></span>
          <span>Gross: <strong>{filteredTotals.gross.toFixed(2)} €</strong></span>
        </div>

        <div className="talent-cn-table-wrap">
          <table className="talent-cn-table">
            <thead>
              <tr>
                <th>Number</th>
                <th>Type</th>
                <th>Status</th>
                <th>Date</th>
                <th>Created</th>
                <th>Created by</th>
                <th>Talent</th>
                <th>Client</th>
                <th>Agency</th>
                <th>AP %</th>
                <th>Net</th>
                <th>VAT</th>
                <th className="credit-notes-col-gross">Gross</th>
                <th>Export</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={14} className="talent-cn-empty">
                    {creditNotes.length === 0
                      ? 'No credit notes yet for the talents you manage.'
                      : 'No credit notes match the current filters.'}
                  </td>
                </tr>
              ) : (
                filtered.map((cn) => (
                  <tr key={cn.id}>
                    <td>{cn.credit_note_number || cn.id}</td>
                    <td>{cn.invoice_type || '—'}</td>
                    <td>{cn.status || '—'}</td>
                    <td>
                      {cn.booking_dates ? (
                        <div className="credit-notes-booking-dates">
                          {cn.booking_dates.split(/\s*,\s*|\n/).filter(Boolean).map((date, i) => (
                            <div key={i}>{date.trim()}</div>
                          ))}
                        </div>
                      ) : formatDate(cn.invoice_date)}
                    </td>
                    <td>
                      {cn.created_at ? (
                        <div className="credit-notes-booking-dates">
                          <div>{formatDate(cn.created_at)}</div>
                          <div>{formatTime(cn.created_at)}</div>
                        </div>
                      ) : '—'}
                    </td>
                    <td>{cn.created_by || '—'}</td>
                    <td>{cn.talent_name || '—'}</td>
                    <td>{getClientName(cn) || '—'}</td>
                    <td>{displayAgency(cn.agency_name)}</td>
                    <td>{cn.agency_percentage != null ? `${Number(cn.agency_percentage)}%` : '—'}</td>
                    <td>{formatMoney(cn.net_amount)}</td>
                    <td>{formatMoney(cn.vat_amount)}</td>
                    <td className="credit-notes-col-gross">{formatMoney(cn.gross_amount)}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() => handleDownloadPdf(cn)}
                        disabled={downloading[`pdf-${cn.id}`]}
                        className="button talent-cn-export-btn"
                      >
                        {downloading[`pdf-${cn.id}`] ? '…' : 'PDF'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDownloadXml(cn)}
                        disabled={downloading[`xml-${cn.id}`]}
                        className="button talent-cn-export-btn"
                      >
                        {downloading[`xml-${cn.id}`] ? '…' : 'XML'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default TalentCreditNotesList;
